import { useSelector } from 'react-redux';

import { Divider, Flex } from '@frontend/legacy-components';

import ControlsInfo from 'modules/common/viewer/cadViewer/addOns/ControlsInfo';
import CrossSectionTool from 'modules/common/viewer/cadViewer/addOns/CrossSectionToolBar';
import DimensionTool from 'modules/common/viewer/cadViewer/addOns/DimensionTool';
import ExplodedViewTool from 'modules/common/viewer/cadViewer/addOns/ExplodedViewToolBar';
import FocusedViewTool from 'modules/common/viewer/cadViewer/addOns/FocusedViewTool';
import HighlightViewTool from 'modules/common/viewer/cadViewer/addOns/HighlightViewTool';
import MeasurementTool from 'modules/common/viewer/cadViewer/addOns/MeasurementTool';
import OptimalBoundingBoxTool from 'modules/common/viewer/cadViewer/addOns/OptimalBoundingBoxTool';
import PMIViewTool from 'modules/common/viewer/cadViewer/addOns/PMIViewTool';
import ResetViewTool from 'modules/common/viewer/cadViewer/addOns/ResetViewTool';
import SelectorTool from 'modules/common/viewer/cadViewer/addOns/SelectorTool';
import WireFrameTool from 'modules/common/viewer/cadViewer/addOns/WireFrameTool';
import DisplayOptionsPopover from 'modules/common/viewer/cadViewer/components/DisplayOptionsPopover';
import { getShowDxfControls } from 'modules/common/viewer/cadViewer/data/reducer';
import {
  getDoesActiveFileHaveOptimalBoundingBox,
  getIsActiveFile3DRenderable,
  getIsActiveFileAssembly,
} from 'modules/common/viewer/data/reducer';

import { useFeatureFlagValue } from 'store/session/hooks';

import { featureFlagKeys } from '../drawers/featureFlagDrawer/const/featureFlagKeys';

const CADViewerTools = () => {
  const doesActiveFileHaveOptimalBoundingBox = useSelector(
    getDoesActiveFileHaveOptimalBoundingBox,
  );
  const isEnabled = useSelector(getIsActiveFile3DRenderable);
  const isActiveFileAssembly = useSelector(getIsActiveFileAssembly);
  const showDxfControls = useSelector(getShowDxfControls);

  /** 3D-only viewer tools (not used for flat DXF–DWG / 2D viewing) */
  const isSolid3dToolbarEnabled = Boolean(isEnabled) && !showDxfControls;

  const isPMIEnabled = useFeatureFlagValue(
    featureFlagKeys.isPmiRenderingEnabled,
  );

  return (
    <Flex
      direction="column"
      position="relative"
      w="100%"
      alignItems="center"
      p={1.25}
      gap={1.5}
    >
      <Flex
        align="center"
        gap={'0.25rem'}
        p={'0.25rem'}
        justify="center"
        minW={0}
        wrap="wrap"
        bg="page.surface"
        border="1px solid"
        borderColor="separator.border"
        borderRadius="0.5rem"
        boxShadow="popover"
      >
        <SelectorTool isEnabled={isEnabled} />
        <MeasurementTool isEnabled={isEnabled} />
        <Divider orientation="vertical" h={4} />
        <DimensionTool isEnabled={isEnabled} />
        <OptimalBoundingBoxTool
          isEnabled={isEnabled && doesActiveFileHaveOptimalBoundingBox}
        />
        <Divider orientation="vertical" h={4} />
        <PMIViewTool isEnabled={isEnabled && isPMIEnabled} />
        <WireFrameTool isEnabled={isSolid3dToolbarEnabled} />
        <HighlightViewTool isEnabled={isSolid3dToolbarEnabled} />
        <FocusedViewTool isEnabled={isSolid3dToolbarEnabled} />
        <Divider orientation="vertical" h={4} />
        <CrossSectionTool
          isEnabled={isSolid3dToolbarEnabled}
          placement="toolbar"
        />
        <ExplodedViewTool
          isCad3d={isSolid3dToolbarEnabled}
          isAssembly={isActiveFileAssembly}
          placement="toolbar"
        />
        {isSolid3dToolbarEnabled && <Divider orientation="vertical" h={4} />}
        <ResetViewTool isEnabled={isEnabled} />
        {isEnabled && <ControlsInfo is2DMode={showDxfControls} />}
        {isEnabled && <DisplayOptionsPopover />}
      </Flex>

      <ExplodedViewTool
        isCad3d={isSolid3dToolbarEnabled}
        isAssembly={isActiveFileAssembly}
        placement="floating"
      />
      <CrossSectionTool
        isEnabled={isSolid3dToolbarEnabled}
        placement="floating"
      />
    </Flex>
  );
};

export default CADViewerTools;
